import { useMemo } from 'react'

import { ApiRequestLimitType } from '@tribeplatform/gql-client/types'
import { useFirstRenderContext } from '@tribeplatform/react-components/common/ssr'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { Link } from '@tribeplatform/react-components/Link'
import {
  useApiUsages,
  useApiRateLimitStats,
} from '@tribeplatform/react-sdk/hooks'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { UsagesSection } from '../components/UsageSection.js'
import { ApiUsageCard } from './ApiUsageCard.js'
import {
  sortApiConsumersByRequestCount,
  getDailyLimitStatForUsageType,
  partitionUsageDataByType,
  generateHeatmapColors,
} from './utils.js'

const CHART_SYNC_ID = 'api-usage-chart-synchronizer-key'

export const ApiUsagesSection = () => {
  const { $t } = useI18n()

  /**
   * Rate limit stats is used to determine the current limits for the API.
   * The usage limits stored on network object are raw values reported by the
   * billing subsystem, which cannot be used for following reasons:
   * 1. For those networks that are missing the limits, it doesn’t take the default values into account.
   * 2. There might be a delay between the time a customer purchases the API add-on, until
   *    it gets reflected in rate limiting module.
   *
   * As result the stats API are the most reliable source of truth to extract
   * the enforced limits.
   */
  const {
    data: rateLimitStats = [],
    isInitialLoading: isLoadingRateLimitStats,
  } = useApiRateLimitStats()

  const { data: apiUsages = [], isInitialLoading: isLoadingUsages } =
    useApiUsages()

  const requestStat = getDailyLimitStatForUsageType(
    rateLimitStats,
    ApiRequestLimitType.Request,
  )

  const complexityStat = getDailyLimitStatForUsageType(
    rateLimitStats,
    ApiRequestLimitType.Complexity,
  )

  const sortedConsumers = sortApiConsumersByRequestCount(apiUsages)

  const { isFirstRender } = useFirstRenderContext()
  const isLoading = isLoadingUsages || isLoadingRateLimitStats

  const colors: Record<string, string> = useMemo(() => {
    if (isFirstRender || isLoading) {
      return {}
    }

    const theme = generateHeatmapColors(sortedConsumers.length)
    return sortedConsumers.reduce((colorMap, clientKey, index) => {
      return {
        ...colorMap,
        [clientKey]: theme.contrastColorValues[index],
      }
    }, {})
  }, [isFirstRender, isLoading, sortedConsumers])

  const { requestCountUsages, complexityUsages } =
    partitionUsageDataByType(apiUsages)

  return (
    <UsagesSection
      title={
        <Text as="h2">
          <T defaultMessage="API Usage" id="Admin.Billing.ApiUsage" />
        </Text>
      }
      description={
        <Text as="p" variant="bodyMd">
          <T
            defaultMessage="See how your API usage compares to your limits."
            id="Admin.Billing.ApiUsage.Description"
          />
        </Text>
      }
    >
      <ApiUsageCard
        stat={requestStat}
        loading={isLoading}
        data={requestCountUsages}
        syncId={CHART_SYNC_ID}
        colors={colors}
        consumers={sortedConsumers}
        title={$t({
          defaultMessage: 'Request count',
          id: 'Admin.Billing.ApiUsage.RequestCount',
        })}
        description={$t({
          defaultMessage: 'The number of API requests made to your site.',
          id: 'Admin.Billing.ApiUsage.RequestCount.Subtitle',
        })}
        finePrint={$t(
          {
            defaultMessage:
              'Visit <link>Developers’ Guide</link> to know more about our rate limits policies.',
            id: 'Admin.Billing.RateLimitsPolicyFinePrint',
          },
          {
            link: chunks => (
              <Link
                variant="accent"
                href="https://developers.bettermode.com/docs/guide/graphql/rate-limits"
                target="_blank"
              >
                {chunks}
              </Link>
            ),
          },
        )}
      />
      <ApiUsageCard
        stat={complexityStat}
        loading={isLoading}
        data={complexityUsages}
        syncId={CHART_SYNC_ID}
        colors={colors}
        consumers={sortedConsumers}
        title={$t({
          defaultMessage: 'Request complexity',
          id: 'Admin.Billing.ApiUsage.RequestComplexity',
        })}
        description={$t({
          defaultMessage: 'The complexity of API requests made to your site',
          id: 'Admin.Billing.ApiUsage.RequestComplexity.Subtitle',
        })}
        finePrint={$t(
          {
            defaultMessage:
              'Learn more about <link>GraphQL Complexity Calculation</link>.',
            id: 'Admin.Billing.GqlRateLimitsFinePrint',
          },
          {
            link: chunks => (
              <Link
                variant="accent"
                href="https://developers.bettermode.com/docs/guide/graphql/rate-limits#graphql-complexity-calculation"
                target="_blank"
              >
                {chunks}
              </Link>
            ),
          },
        )}
      />
    </UsagesSection>
  )
}
